import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { authAPI, getUser } from './services/authApi';
import { notificationAPI, searchAPI } from './services/api';
import SessionManager from './components/SessionManager';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import { ToastProvider } from './components/Toast';
import { initScrollIndicators } from './utils/scrollIndicators';
import '../src/utils/offlineQueue'; // register online listener
import { ROUTES, PUBLIC_ROUTES } from './constants/routes';

// Lazy-loaded page components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CattleList = lazy(() => import('./pages/CattleList'));
const CattleDetail = lazy(() => import('./pages/CattleDetail'));
const PastureList = lazy(() => import('./pages/PastureList'));
const PastureDetail = lazy(() => import('./pages/PastureDetail'));
const VetVisits = lazy(() => import('./pages/VetVisits'));
const BreedingRecords = lazy(() => import('./pages/BreedingRecords'));
const FeedLotList = lazy(() => import('./pages/FeedLotList'));
const FeedLotDetail = lazy(() => import('./pages/FeedLotDetail'));
const HayFieldList = lazy(() => import('./pages/HayFieldList'));
const HayFieldDetail = lazy(() => import('./pages/HayFieldDetail'));
const HayFieldInfoList = lazy(() => import('./pages/HayFieldInfoList'));
const HayFieldInfoDetail = lazy(() => import('./pages/HayFieldInfoDetail'));
const EquipmentList = lazy(() => import('./pages/EquipmentList'));
const EquipmentDetail = lazy(() => import('./pages/EquipmentDetail'));
const Chores = lazy(() => import('./pages/Chores'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Medications = lazy(() => import('./pages/Medications'));
const VaccinationProtocols = lazy(() => import('./pages/VaccinationProtocols'));
const GrazingRotations = lazy(() => import('./pages/GrazingRotations'));
const Documents = lazy(() => import('./pages/Documents'));
const Reports = lazy(() => import('./pages/Reports'));
const Analytics = lazy(() => import('./pages/Analytics'));
const PastureMap = lazy(() => import('./pages/PastureMap'));
const Calculators = lazy(() => import('./pages/Calculators'));
const BulkOperations = lazy(() => import('./pages/BulkOperations'));
const BreedSubmissions = lazy(() => import('./pages/BreedSubmissions'));
const CattleGroups = lazy(() => import('./pages/CattleGroups'));
const CattleGroupDetail = lazy(() => import('./pages/CattleGroupDetail'));
const BreedingPlans = lazy(() => import('./pages/BreedingPlans'));
const Admin = lazy(() => import('./pages/Admin'));

function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchIndex, setSearchIndex] = useState(-1);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const controller = new AbortController();
    const userData = getUser();
    setUser(userData);
    if (userData) {
      notificationAPI.getAll().then(res => {
        if (!controller.signal.aborted) {
          const unread = (res.data || []).filter(n => !n.read).length;
          setUnreadCount(unread);
        }
      }).catch(err => {
        if (!controller.signal.aborted) {
          console.error('Failed to fetch notifications:', err);
        }
      });
    }
    setMobileMenuOpen(false);
    setSearchOpen(false);
    setSearchResults(null);
    setSearchQuery('');
    return () => controller.abort();
  }, [location]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    try {
      const res = await searchAPI.global(searchQuery.trim());
      setSearchResults(res.data);
      setSearchOpen(true);
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  const handleSearchResultClick = (type, id) => {
    setSearchOpen(false);
    setSearchResults(null);
    setSearchQuery('');
    setSearchIndex(-1);
    if (type === 'cattle') navigate(`/cattle/${id}`);
    else if (type === 'equipment') navigate(`/equipment/${id}`);
    else if (type === 'pasture') navigate(`/pastures/${id}`);
    else if (type === 'vet_visit') navigate(ROUTES.VET_VISITS);
  };

  // Flatten search results for keyboard navigation
  const flatResults = searchResults ? Object.entries(searchResults).flatMap(([type, items]) =>
    (items || []).map(item => ({ type, ...item }))
  ) : [];

  const handleSearchKeyDown = (e) => {
    if (!searchOpen || flatResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSearchIndex(prev => (prev + 1) % flatResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSearchIndex(prev => (prev <= 0 ? flatResults.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && searchIndex >= 0) {
      e.preventDefault();
      const item = flatResults[searchIndex];
      handleSearchResultClick(item.type, item.id);
    } else if (e.key === 'Escape') {
      setSearchOpen(false);
      setSearchResults(null);
      setSearchIndex(-1);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate(ROUTES.LOGIN);
  };

  if (PUBLIC_ROUTES.some(r => location.pathname === r || (r === ROUTES.RESET_PASSWORD && location.pathname.startsWith(r)))) {
    return null;
  }

  return (
    <nav className="navbar">
      <div className="navbar-top">
        <Link to="/" className="navbar-brand">
          <img src="/logo.png" alt="Cattle Management" style={{ height: '36px', width: 'auto', marginRight: '8px', verticalAlign: 'middle' }} />
          Cattle Mgmt
        </Link>
        <form onSubmit={handleSearch} className="navbar-search" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="navbar-search-input"
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '16px' }}
          />
          {searchOpen && searchResults && (() => {
            let flatIdx = 0;
            return (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-primary, white)', border: '1px solid var(--border-color, #d1d5db)', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, maxHeight: '300px', overflowY: 'auto', minWidth: '250px', maxWidth: 'calc(100vw - 32px)' }}>
              {Object.keys(searchResults).every(k => (searchResults[k]?.length || 0) === 0) ? (
                <div style={{ padding: '10px', color: '#6b7280', textAlign: 'center' }}>No results found</div>
              ) : (
                Object.entries(searchResults).map(([type, items]) => (
                  items && items.length > 0 && (
                    <div key={type}>
                      <div style={{ padding: '6px 10px', background: 'var(--bg-secondary, #f3f4f6)', fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'capitalize' }}>{type.replace('_', ' ')}</div>
                      {items.map(item => {
                        const currentIdx = flatIdx++;
                        const isHighlighted = currentIdx === searchIndex;
                        return (
                        <div key={item.id} onClick={() => handleSearchResultClick(type, item.id)} style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border-color, #f3f4f6)', background: isHighlighted ? 'var(--bg-hover, #eff6ff)' : 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover, #eff6ff)'; setSearchIndex(currentIdx); }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                          {item.tag_number || item.name || `#${item.id}`}
                          {item.name && item.tag_number ? ` - ${item.name}` : ''}
                        </div>
                        );
                      })}
                    </div>
                  )
                ))
              )}
              <div style={{ padding: '6px 10px', textAlign: 'center' }}>
                <button type="button" onClick={() => { setSearchOpen(false); setSearchResults(null); setSearchIndex(-1); }} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.85rem' }}>Close</button>
              </div>
            </div>
            );
          })()}
        </form>
        <div className="navbar-utils">
          <button className="theme-toggle" onClick={() => setDarkMode(!darkMode)} title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            {darkMode ? '☀️' : '🌙'}
          </button>
          {user && (
            <>
              <Link to="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} title="Notifications" style={{ position: 'relative', color: '#d1d5db', textDecoration: 'none', fontSize: '18px' }}>
                🔔{unreadCount > 0 && <span style={{ position: 'absolute', top: '-6px', right: '-8px', background: '#dc2626', color: 'white', borderRadius: '50%', width: '16px', height: '16px', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unreadCount}</span>}
              </Link>
              <Link to="/profile" className="navbar-profile-link" style={{ color: '#d1d5db', textDecoration: 'none', fontSize: '0.85rem' }}>
                {user.username}
              </Link>
              <button onClick={handleLogout} className="btn btn-secondary btn-sm" style={{ padding: '4px 12px', fontSize: '0.8rem' }}>Logout</button>
            </>
          )}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>
      <div className={`navbar-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <Link to="/">Dashboard</Link>
        <span className="nav-divider">|</span>
        <Link to="/cattle">Cattle</Link>
        <Link to="/pastures">Pastures</Link>
        <Link to="/grazing-rotations">Grazing</Link>
        <Link to="/feedlots">Feed Lots</Link>
        <span className="nav-divider">|</span>
        <Link to="/hay-field-info">Hay Fields</Link>
        <Link to="/hay-fields">Hay Cuttings</Link>
        <Link to="/equipment">Equipment</Link>
        <span className="nav-divider">|</span>
        <Link to="/vet-visits">Vet Visits</Link>
        <Link to="/breeding-records">Breeding</Link>
        <Link to="/breeding-plans">Breeding Plans</Link>
        <Link to="/vaccination-protocols">Vacc Protocols</Link>
        <Link to="/medications">Medications</Link>
        <span className="nav-divider">|</span>
        <Link to="/chores">Chores</Link>
        <Link to="/documents">Documents</Link>
        <Link to="/reports">Reports</Link>
        <span className="nav-divider">|</span>
        <Link to="/analytics">Analytics</Link>
        <Link to="/cattle-groups">Calf Groups</Link>
        <Link to="/map">Map</Link>
        <Link to="/bulk-operations">Bulk Ops</Link>
        <Link to="/breed-submissions">Breed/USDA</Link>
        {user && user.role === 'admin' && (
          <>
            <span className="nav-divider">|</span>
            <Link to="/users">Users</Link>
            <Link to="/settings">Settings</Link>
            <Link to="/admin">Admin</Link>
          </>
        )}
      </div>
    </nav>
  );
}

function App() {
  useEffect(() => {
    return initScrollIndicators();
  }, []);

  return (
    <ErrorBoundary>
    <Router>
    <ToastProvider>
      <div className="App">
        <SessionManager />
        <NavBar />
        <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'60vh'}}><div>Loading...</div></div>}>
        <Routes>
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
          <Route path={ROUTES.DASHBOARD} element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path={ROUTES.CATTLE} element={<ProtectedRoute><CattleList /></ProtectedRoute>} />
          <Route path={ROUTES.CATTLE_DETAIL} element={<ProtectedRoute><CattleDetail /></ProtectedRoute>} />
          <Route path={ROUTES.PASTURES} element={<ProtectedRoute><PastureList /></ProtectedRoute>} />
          <Route path={ROUTES.PASTURE_DETAIL} element={<ProtectedRoute><PastureDetail /></ProtectedRoute>} />
          <Route path={ROUTES.FEEDLOTS} element={<ProtectedRoute><FeedLotList /></ProtectedRoute>} />
          <Route path={ROUTES.FEEDLOT_DETAIL} element={<ProtectedRoute><FeedLotDetail /></ProtectedRoute>} />
          <Route path={ROUTES.HAY_FIELD_INFO} element={<ProtectedRoute><HayFieldInfoList /></ProtectedRoute>} />
          <Route path={ROUTES.HAY_FIELD_INFO_DETAIL} element={<ProtectedRoute><HayFieldInfoDetail /></ProtectedRoute>} />
          <Route path={ROUTES.HAY_FIELDS} element={<ProtectedRoute><HayFieldList /></ProtectedRoute>} />
          <Route path={ROUTES.HAY_FIELD_DETAIL} element={<ProtectedRoute><HayFieldDetail /></ProtectedRoute>} />
          <Route path={ROUTES.EQUIPMENT} element={<ProtectedRoute><EquipmentList /></ProtectedRoute>} />
          <Route path={ROUTES.EQUIPMENT_DETAIL} element={<ProtectedRoute><EquipmentDetail /></ProtectedRoute>} />
          <Route path={ROUTES.VET_VISITS} element={<ProtectedRoute><VetVisits /></ProtectedRoute>} />
          <Route path={ROUTES.BREEDING_RECORDS} element={<ProtectedRoute><BreedingRecords /></ProtectedRoute>} />
          <Route path={ROUTES.MEDICATIONS} element={<ProtectedRoute><Medications /></ProtectedRoute>} />
          <Route path={ROUTES.CHORES} element={<ProtectedRoute><Chores /></ProtectedRoute>} />
          <Route path={ROUTES.USERS} element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
          <Route path={ROUTES.PROFILE} element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path={ROUTES.SETTINGS} element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path={ROUTES.VACCINATION_PROTOCOLS} element={<ProtectedRoute><VaccinationProtocols /></ProtectedRoute>} />
          <Route path={ROUTES.GRAZING_ROTATIONS} element={<ProtectedRoute><GrazingRotations /></ProtectedRoute>} />
          <Route path={ROUTES.DOCUMENTS} element={<ProtectedRoute><Documents /></ProtectedRoute>} />
          <Route path={ROUTES.REPORTS} element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path={ROUTES.ANALYTICS} element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path={ROUTES.MAP} element={<ProtectedRoute><PastureMap /></ProtectedRoute>} />
          <Route path={ROUTES.CALCULATORS} element={<ProtectedRoute><Calculators /></ProtectedRoute>} />
          <Route path={ROUTES.BULK_OPERATIONS} element={<ProtectedRoute><BulkOperations /></ProtectedRoute>} />
          <Route path={ROUTES.BREED_SUBMISSIONS} element={<ProtectedRoute><BreedSubmissions /></ProtectedRoute>} />
          <Route path={ROUTES.CATTLE_GROUPS} element={<ProtectedRoute><CattleGroups /></ProtectedRoute>} />
          <Route path={ROUTES.CATTLE_GROUP_DETAIL} element={<ProtectedRoute><CattleGroupDetail /></ProtectedRoute>} />
          <Route path={ROUTES.BREEDING_PLANS} element={<ProtectedRoute><BreedingPlans /></ProtectedRoute>} />
          <Route path={ROUTES.ADMIN} element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        </Routes>
        </Suspense>
      </div>
    </ToastProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
